import {type InterestRecommendation} from '@dms/api/interest-recommendations';
import i18n from '@dms/i18n';

import {type MyDayData} from '../types/MyDayData';

const EVALUATED_AT = '2026-10-28T07:00:00.000Z';

const BASE_RECOMMENDATION = {
  interestType: 'sales',
  vehicleId: null,
  state: 'open',
  leadQuality: 'hot',
  createdAt: '2026-10-20T08:00:00.000Z',
  updatedAt: null,
  score: null,
  omissions: [],
  decisions: [],
} satisfies Partial<InterestRecommendation>;

const PREVIEW_ITEMS = [
  {
    id: '1',
    customer: 'Jana Nováková',
    vehicle: 'Škoda Octavia Combi 2.0 TDI',
    channel: 'email',
    signal: 'customerAwaitingReply',
    severity: 'danger',
    evidence: {waitingSinceHours: 16},
  },
  {
    id: '2',
    customer: 'Eva Dvořáková',
    vehicle: 'Toyota RAV4 Hybrid',
    channel: 'call',
    signal: 'noFirstResponse',
    severity: 'danger',
    evidence: {waitingSinceHours: 2},
  },
  {
    id: '3',
    customer: 'Markus Weber',
    vehicle: 'BMW X3 xDrive20d',
    channel: 'email',
    signal: 'engagementDetected',
    severity: 'info',
    evidence: {engagementKind: 'offerOpened'},
  },
  {
    id: '4',
    customer: 'Lukáš Černý',
    vehicle: 'Kia Ceed SW 1.5 T-GDI',
    channel: 'sms',
    signal: 'overdueTask',
    severity: 'warning',
    evidence: {overdueTaskCount: 1},
  },
  {
    id: '5',
    customer: 'Tomasz Kowalski',
    vehicle: 'Volkswagen Passat Variant',
    channel: 'sms',
    signal: 'goingCold',
    severity: 'warning',
    evidence: {inactiveForDays: 8},
  },
  {
    id: '6',
    customer: 'Petr Svoboda',
    vehicle: 'Hyundai Tucson 1.6 T-GDI',
    channel: 'call',
    signal: 'missingNextStep',
    severity: 'info',
    evidence: {},
  },
] satisfies {
  id: string;
  customer: string;
  vehicle: string;
  channel: InterestRecommendation['recommendedChannel']['channel'];
  signal: InterestRecommendation['signals'][number]['code'];
  severity: InterestRecommendation['signals'][number]['severity'];
  evidence: InterestRecommendation['signals'][number]['evidence'];
}[];

/** A complete sample snapshot. No customer IDs here identify real DMS records. */
export const MY_DAY_PREVIEW_DATA: MyDayData = {
  evaluatedAt: EVALUATED_AT,
  recommendations: PREVIEW_ITEMS.map((item) => ({
    ...BASE_RECOMMENDATION,
    interestId: `preview-interest-${item.id}`,
    interestCode: `ZJ-2026-040${item.id}`,
    customerId: `preview-customer-${item.id}`,
    customerLabel: item.customer,
    signals: [
      {
        code: item.signal,
        severity: item.severity,
        evaluatedAt: EVALUATED_AT,
        evidenceIncomplete: false,
        evidenceOmissions: [],
        evidence: item.evidence,
      },
    ],
    recommendedChannel: {
      channel: item.channel,
      reasonCode: item.id === '2' ? 'noInboundContact' : 'lastInboundChannel',
    },
  })),
  coverage: {eligible: 34, evaluated: 34, skipped: 0, failed: 0, omissions: []},
  hasMoreRecommendations: false,
  totals: {recommended: 6, awaitingReply: 2, atRisk: 2},
  contextByInterestId: Object.fromEntries(
    PREVIEW_ITEMS.map((item) => [
      `preview-interest-${item.id}`,
      {
        vehicleLabel: item.vehicle,
        lastActivity: {
          label: i18n.t('entity.interestRecommendations.factors.recentCustomerContact'),
          occurredAt: '2026-10-27T14:42:00.000Z',
        },
        draft: i18n.t('page.interestMyDay.preview.draft', {
          vehicle: item.vehicle,
        }),
      },
    ])
  ),
  history: {
    day: '2026-10-28',
    timeZone: 'Europe/Prague',
    hasMore: false,
    isComplete: true,
    entries: [
      {
        id: 'preview-decision-1',
        interestId: 'preview-handled-1',
        customerLabel: 'Martin Dvořák',
        actorLabel: 'Petr Malý',
        outcome: 'completed',
        occurredAt: '2026-10-28T06:40:00.000Z',
        canUndo: false,
      },
      {
        id: 'preview-decision-2',
        interestId: 'preview-handled-2',
        customerLabel: 'Lucie Králová',
        actorLabel: 'Petr Malý',
        outcome: 'attempted',
        occurredAt: '2026-10-28T06:45:00.000Z',
        canUndo: false,
      },
      {
        id: 'preview-decision-3',
        interestId: 'preview-handled-3',
        customerLabel: 'Jan Procházka',
        actorLabel: 'Petr Malý',
        outcome: 'rejected',
        rejectionReason: 'BAD_TIMING',
        occurredAt: '2026-10-28T06:50:00.000Z',
        canUndo: false,
      },
    ],
  },
  saleFunnel: {newInterests: 8, activeInterests: 11, offers: 7, contracts: 3, sold: 4, closed: 2},
};
